'use client';
import React, { useEffect, useState } from 'react';
import { Activity } from 'lucide-react';
import EmptyState from '@/components/ui/EmptyState';
import { createClient } from '@/lib/supabase/client';

interface ActivityItem {
  id: string;
  type: 'completion' | 'payment' | 'registration' | 'reschedule' | 'advance';
  message: string;
  time: string;
  actor: string;
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function mapRowToActivity(row: any): ActivityItem {
  const typeMap: Record<string, ActivityItem['type']> = {
    completed: 'completion',
    rescheduled: 'reschedule',
    scheduled: 'registration',
    confirmed: 'registration',
    'in-progress': 'registration',
  };
  const messageMap: Record<string, string> = {
    completed: `Job completed for ${row.customer_name}`,
    rescheduled: `Appointment rescheduled for ${row.customer_name}`,
    scheduled: `New appointment scheduled for ${row.customer_name}`,
    confirmed: `Appointment confirmed for ${row.customer_name}`,
    'in-progress': `Technician started job for ${row.customer_name}`,
  };
  return {
    id: row.id,
    type: typeMap[row.status] || 'registration',
    message: messageMap[row.status] || `Appointment updated for ${row.customer_name}`,
    time: formatRelativeTime(row.updated_at || row.created_at),
    actor: row.assigned_technician_name || 'System',
  };
}

export default function ActivityFeed() {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const supabase = createClient();

  useEffect(() => {
    // Initial load: last 20 appointment events ordered by most recent
    async function fetchRecentActivity() {
      const { data } = await supabase
        .from('appointments')
        .select('id, status, customer_name, assigned_technician_name, updated_at, created_at')
        .order('updated_at', { ascending: false })
        .limit(20);

      if (data) {
        setActivities(data.map(mapRowToActivity));
      }
    }
    fetchRecentActivity();

    // Real-time: prepend new events as they arrive
    const channel = supabase
      .channel('activity-feed-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'appointments' },
        (payload) => {
          if (payload.new && typeof payload.new === 'object') {
            const newItem = mapRowToActivity(payload.new as any);
            setActivities((prev) => {
              // Replace if same id exists, otherwise prepend
              const exists = prev.findIndex((a) => a.id === newItem.id);
              if (exists !== -1) {
                const updated = [...prev];
                updated[exists] = newItem;
                return updated;
              }
              return [newItem, ...prev].slice(0, 20);
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <div className="bg-card rounded-xl border border-border flex flex-col h-full">
      <div className="px-5 py-4 border-b border-border">
        <h2 className="text-base font-700 text-foreground">Recent Activity</h2>
        <p className="text-xs text-muted-foreground mt-0.5">System-wide event log</p>
      </div>
      {activities.length === 0 ? (
        <EmptyState
          icon={<Activity size={24} />}
          title="No activity yet"
          description="System events will appear here as customers register, jobs are completed, and payments are verified."
        />
      ) : (
        <div className="flex-1 overflow-y-auto divide-y divide-border">
          {activities.map((item) => (
            <div key={item.id} className="px-5 py-3 hover:bg-muted/30 transition-colors">
              <p className="text-sm text-foreground">{item.message}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground">{item.actor}</span>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="text-xs text-muted-foreground">{item.time}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}